import axios from "axios";
import { API_URL } from "@/config";
import { getOptionsCacheKey } from "@/utils/cache";
import { resetStateToDefault } from "@/lib/store/index";

const defaultState = Object.freeze({
  institutionsIdsWithHiddenGrammages: [],
  options: [],
  allOptions: [],
  lastOptionsCriteria: null,
});

export default {
  namespaced: true,

  state: {
    ...defaultState,
  },

  getters: {
    getAllOptions(state) {
      return state.allOptions;
    },
    showShortNames(_, __, ___, rootGetters) {
      return rootGetters["auth/isAuthenticated"] && rootGetters["auth/isCustomer"] === false;
    },
  },

  mutations: {
    setInstitutionsIdsWithHiddenGrammages(state, institutionsIds) {
      state.institutionsIdsWithHiddenGrammages = institutionsIds;
    },

    setOptions(state, { options, criteria }) {
      state.options = options;
      state.lastOptionsCriteria = getOptionsCacheKey(criteria);
    },
    setAllOptions(state, { options }) {
      state.allOptions = options;
    },

    clearOptionsCache(state) {
      state.options = [];
    },

    resetState(state) {
      resetStateToDefault(state, defaultState);
    },
  },

  actions: {
    async getMany(_, criteria) {
      const response = await axios.get(`${API_URL}/institutions`, { params: criteria });

      if (!response.data || typeof response.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOne(_, institutionId) {
      const response = await axios.get(`${API_URL}/institutions/${institutionId}`);

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async create({ commit }, institutionData) {
      const response = await axios.post(`${API_URL}/institutions`, institutionData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async update({ commit }, { institutionId, institutionData }) {
      const response = await axios.put(`${API_URL}/institutions/${institutionId}`, institutionData);

      if (response.status !== 200 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async delete({ commit }, institutionId) {
      const response = await axios.delete(`${API_URL}/institutions/${institutionId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return true;
    },

    async getInstitutionsWithoutGivenClassroomName(_, classroomId) {
      const response = await axios.get(
        `${API_URL}/institutions/withoutClassroomName/${classroomId}`,
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getInstitutionTermsUrl(_, institutionId) {
      const response = await axios.get(`${API_URL}/institutions/${institutionId}/termsUrl`);

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async activate({ commit }, institutionId) {
      const response = await axios.post(`${API_URL}/institutions/${institutionId}/activate`);

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async deactivateGlobally({ commit }, institutionId) {
      const response = await axios.post(
        `${API_URL}/institutions/${institutionId}/deactivateGlobally`,
      );

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async hideForUsers({ commit }, institutionId) {
      const response = await axios.post(`${API_URL}/institutions/${institutionId}/hideForUsers`);

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async showForUsers({ commit }, institutionId) {
      const response = await axios.post(`${API_URL}/institutions/${institutionId}/showForUsers`);

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async updateRegistrationAvailability({ commit }, { institutionId, registrationAvailability }) {
      const response = await axios.put(
        `${API_URL}/institutions/${institutionId}/registrationAvailability`,
        { registrationAvailability },
      );

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return response.data;
    },

    async getOptions({ commit, state }, criteria = {}) {
      const cacheKey = getOptionsCacheKey(criteria);

      if (state.options?.length && state.lastOptionsCriteria === cacheKey) {
        return state.options;
      }

      const response = await axios.get(`${API_URL}/institutions/options`, {
        params: criteria,
      });

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setOptions", { options: response.data, criteria });

      return response.data;
    },
    async getAllOptions({ commit, state, rootGetters }) {
      if (state.allOptions?.length) {
        return state.allOptions;
      }

      const response = await axios.get(`${API_URL}/institutions/options`, {
        params: {
          // Used for the child add/edit institution selector: must not be scoped to the
          // parent's own institutions, otherwise a parent whose only institution gets hidden
          // has no institution left to choose from when adding a new child.
          showAll: true,
          // Parents must never see institutions hidden for users; admins/staff always see them normally.
          hiddenForUsers: rootGetters["auth/isParent"] ? false : undefined,
        },
      });

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setAllOptions", { options: response.data });

      return response.data;
    },

    async getInstitutionOrderingNote(_, institutionId) {
      const response = await axios.get(`${API_URL}/institutions/${institutionId}/orderingNote`);

      if (!response.status || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async fetchInstitutionsIdsWithHiddenGrammages({ commit }) {
      const response = await axios.get(
        `${API_URL}/institutionSettings/institutions/hiddenGrammages`,
      );

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setInstitutionsIdsWithHiddenGrammages", response.data);
    },

    shouldHideGrammages({ state }, institutionId) {
      if (!Array.isArray(institutionId)) {
        return state.institutionsIdsWithHiddenGrammages.includes(institutionId);
      }

      return institutionId.every((id) => state.institutionsIdsWithHiddenGrammages.includes(id));
    },

    async resetClassrooms(_, institutionId) {
      const response = await axios.post(
        `${API_URL}/institutions/${institutionId}/classrooms/reset`,
      );

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getInstitutionRegularPaymentsBankAccountNumber(_, institutionId) {
      const response = await axios.get(
        `${API_URL}/institutions/${institutionId}/regularPaymentsBankAccountNumber`,
      );

      if (!response.status || response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data ?? null;
    },
  },
};
